<template>
  <div class="org-list jps-layout-sm-content">
    <div class="jps-block" v-if="!userStore.isSysad">
      <button class="jps-round-button jps-blue" @click="newProject()">
        新しいプロジェクトを作成する
      </button>
      <UiManagementProjectNewModal ref="managementProjectNewModal" />
    </div>
    <div v-if="ss.searched">
      <div class="search-control">
        <div>{{ ss.state.result.hit }}件</div>
        <SearchPagination :ss="ss" />
        <div class="search-box">
          <SearchBoxSimple :ss="ss" />
        </div>
      </div>
      <div class="jps-message">
        プロジェクトの操作を行う場合には、アカウントメニューからプロジェクト用のアカウントに切り替えてください。
      </div>
      <UiTable
        v-if="ss.searched"
        :data="ss.state.result.list"
        :columns="columns"
        class="ui-sortable-search-result-table"
      >
        <template #column-name="sp">
          <BaseLink :to="'/project/' + sp.row.id">
            {{ ls(sp.row.name) }}
          </BaseLink>
        </template>
        <template #column-org="sp">
          <UiOrgSpan :id="sp.row.permission.ownerOrg" />
        </template>
      </UiTable>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
import type { ColumnDef } from '~/components/ui/UiTable.vue'
import type { UiManagementProjectNewModal } from '#components'

const { ls, lss, led } = useI18n()
const userStore = useUserStore()
const { loadingExecuteAndNotify } = useLoader()
const { snackbarError } = useSnackbar()
const { searchOrganization, createProject } = useOrganization()

interface Props {
}
const props = withDefaults(defineProps<Props>(), {
})

const ss:Ref<SearchStore<Organization>> = ref(null)

onBeforeMount(async () => {
  ss.value = useSearch<Database>(
    { keywordFields: ['keywords'] },
    searchOrganization,
    {
      configNoCache: true,
      queryRewriter: (q: SearchQuery) => {
        q.permission = 'O'
        q.filter = { type: ['PROJECT'] }
        return q
      }
    }
  )
  await ss.value.restoreQuery()
})

const managementProjectNewModal = ref<InstanceType<typeof UiManagementProjectNewModal>>()
const newProject = async () => {
  let proj = null
  try {
    proj = await managementProjectNewModal.value.openAwait()
  } catch (cancel) {
    return
  }
  if (!proj) return
  try {
    await loadingExecuteAndNotify(
      'プロジェクトを作成しました',
      createProject(proj)
    )
    ss.value.executeRefresh()
  } catch (error) {
    snackbarError({ ja: '作成に失敗:' + error.code })
  }
}

const columns: ColumnDef[] = [
  {
    label: () => ls(dapLangGlobal.name),
    key: 'name',
  },
  {
    label: () => lss('作成機関', 'Organization'),
    key: 'org',
  },
  {
    label: () => '説明',
    path: ['descripiton'],
  },
  {
    label: () => ls(dapLangGlobal.public),
    path: ['permission.publicationLevel'],
    display: (e) => led(PUBLICATION_LEVEL_DEF, e),
  },
  {
    label: () => ls(dapLangGlobal.permission),
    path: ['permission.level'],
    display: (e) => led(PERMISSION_LEVEL_DEF, e),
  },
]
</script>

<style lang="scss" scoped>
.org-list {
  .search-control {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .search-box {
    flex-grow: 1;
    display: flex;
    justify-content: flex-end;
  }
  .filter {
    display: flex;
  }

  :deep(th) {
    white-space: nowrap;
    font-size: 0.75rem;
    font-weight: normal;
    text-align: left;
    > * {
      vertical-align: middle;
    }
  }
}
</style>
